import {
  AssessmentTestSection,
  AssessmentTestStepAnswer,
} from '../../../api/admin';

type SectionAnswer = AssessmentTestStepAnswer['sections'][number];

/**
 * Produces a readable, verbatim stringification of a learner's submitted answer
 * for a given section. Mirrors the per-type handling used by
 * `QuestionAnswerCell`, but returns a plain string so it can be rendered inline
 * (e.g. as markdown) outside of a table cell. Eval/scoring independent — works
 * for completed-but-unscored sessions.
 */
export function formatSectionAnswer(
  answer: SectionAnswer | null | undefined,
  section: AssessmentTestSection
): string {
  if (!answer) {
    return '';
  }

  const type = answer.type;

  if (type === 'text-input' || type === 'py-editor' || type === 'llm-editor') {
    // `py-editor`/`llm-editor` answers may arrive as an object from simulations.
    const value = answer.answer as unknown;
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object') {
      const obj = value as { code?: string; prompt?: string };
      return obj.code ?? obj.prompt ?? '';
    }
    return '';
  }

  if (type === 'ranking' || type === 'multiple-choice') {
    const options = answer.answer ?? [];
    return options
      .map((option, index) => `${index + 1}. ${option.content}`)
      .join('\n');
  }

  if (type === 'multiple-rating') {
    const answers = answer.answer ?? [];
    return answers
      .map((entry, index) => `${index + 1}. ${entry?.label ?? '-'}`)
      .join('\n');
  }

  if (type === 'matching') {
    const matchingSection =
      section.type === 'matching'
        ? section
        : { leftItems: [], rightItems: [] };
    const pairs = (answer.answer ?? []).map((pair) => ({
      left:
        matchingSection.leftItems.find((item) => item.id === pair.leftId)
          ?.content ?? '-',
      right:
        matchingSection.rightItems.find((item) => item.id === pair.rightId)
          ?.content ?? '-',
    }));
    return pairs
      .map((pair, index) => `${index + 1}. ${pair.left} -> ${pair.right}`)
      .join('\n');
  }

  if (type === 'fill-in-the-gap' || type === 'text-template') {
    const blanks = answer.answer ?? [];
    return blanks
      .map((blank, index) => `${index + 1}. ${blank?.content ?? '-'}`)
      .join('\n');
  }

  if (type === 'py-playground') {
    const code = answer.answer?.code ?? '';
    const output = answer.answer?.output ?? '';
    return `${code}\n\n# Output\n\n${output}`;
  }

  if (type === 'llm-playground') {
    const output = answer.answer?.output ?? '';
    const prompt = answer.answer?.prompt ?? '';
    return `${output}\n\n# Prompt\n\n${prompt}`;
  }

  if (type === 'matrix-ranking') {
    const items = answer.answer ?? [];
    return items
      .map(
        (item, index) =>
          `${index + 1}. ${item?.x}, ${item?.y}, ${item?.note ?? '-'}`
      )
      .join('\n');
  }

  return '';
}
